import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
  Search,
  Users,
  Download,
  LayoutGrid,
  GraduationCap,
  X,
  ArrowRightLeft,
  PowerOff,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Badge, Modal } from '../../Components/ui';
import { useToast, useConfirm } from '../../Components/Feedback';
import { corDaAtividade } from '../../utils/corDaAtividade';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { corCardNivel } from '../../utils/niveis';
import { useGradeTurmasFiltros } from './GradeTurmasContext';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const HORARIOS = ['HR 1', 'HR 2', 'HR 3'];
const TURNOS = ['Manhã', 'Tarde', 'Noite'];

// Nível não é uma coluna própria em `atividades` — vem embutido no nome da
// turma (convenção "E.P. - N1.2 - D", "ESP - N2 - R", "ESP - N3 e N4"...),
// só pras áreas Educacional/Esportivo; Cultural/Tecnológico/Capelania não
// usam nível. Uma turma pode ter mais de um (ex. "N3 e N4"), por isso
// devolve um array — e o regex captura os dois de "N3 e N4" naturalmente,
// sem precisar de um caso especial pro "e".
const nivelDaTurma = (nome) => {
  const encontrados = (nome || '').match(/N\d+(?:\.\d+)?/gi);
  return encontrados ? encontrados.map((n) => n.toUpperCase()) : [];
};

// Ordena "N2" antes de "N2.1" antes de "N3", numericamente (não como texto,
// senão "N10" viria antes de "N2").
const compararNiveis = (a, b) => {
  const [aMajor, aMinor = 0] = a.slice(1).split('.').map(Number);
  const [bMajor, bMinor = 0] = b.slice(1).split('.').map(Number);
  return aMajor - bMajor || aMinor - bMinor;
};

// Tela de grade horária por turma. "Turma" aqui = uma atividade num horário
// específico (nome + dia_semana + horario + turno), que é o conceito real de
// turma do sistema — o campo "turma" (A/B) do cadastro de aluno é só uma
// etiqueta de divisão por tamanho, sem horário próprio, e por isso não
// organiza mais esta tela (ver conversa que motivou essa mudança).
//
// Desde a migração migrate-split-atividades-por-horario.js, GET /api/atividades
// já devolve uma linha por turma real, com professor e contagem de alunos
// prontos — sem precisar cruzar com /api/grade ou /api/alunos no cliente.
//
// Organização: abas por Turno, e um alternador Atividade/Professor que troca
// SÓ a forma de agrupar os mesmos dados (não é uma tela separada — os dois
// modos usam a mesma busca já carregada, só renderizam diferente):
//   - Atividade: grade dia×horário com as turmas, painel lateral por professor.
//   - Professor: uma mini-grade por professor, painel lateral por atividade.
function GradeTurmas() {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeEditar, podeExportar } = usePermissao('/grade-turmas');
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const authHeaders = useMemo(
    () => ({
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );
  const api = useApi();

  // Turno, modo, buscas e filtro de área vivem num Context acima do Router
  // (ver GradeTurmasContext.js) — de propósito, pra sobreviver quando o
  // usuário troca de menu e volta pra essa tela. Turma sem área definida (ex.:
  // criada antes desse campo existir) sempre aparece, não some com nenhum filtro.
  const {
    turnoSelecionado,
    setTurnoSelecionado,
    modo,
    setModo,
    buscaProfessor,
    setBuscaProfessor,
    buscaAtividade,
    setBuscaAtividade,
    areasAtivas,
    setAreasAtivas,
    niveisSelecionados,
    setNiveisSelecionados,
  } = useGradeTurmasFiltros();
  const alternarArea = (value) => setAreasAtivas((prev) => ({ ...prev, [value]: !prev[value] }));
  const alternarNivel = (nivel) =>
    setNiveisSelecionados((prev) =>
      prev.includes(nivel) ? prev.filter((n) => n !== nivel) : [...prev, nivel],
    );

  const { data: atividades = [], isLoading } = useQuery({
    queryKey: ['atividades-grade-turmas', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/atividades`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
    // Essas turmas podem ter sido alteradas em outra tela (Ajuste de Grade,
    // Grade) que usa sua própria chave de cache — refetchOnMount 'always'
    // ignora o staleTime padrão (5min, ver App.js) e busca de novo toda vez
    // que essa tela é aberta, sem bloquear a exibição do que já estava em cache.
    refetchOnMount: 'always',
    // refetchOnMount só ajuda se a tela for aberta DE NOVO — se ela já estava
    // aberta numa aba (ex.: usuário editou em Turmas.js numa aba e essa aba
    // ficou parada em segundo plano), nada dispara um novo fetch até o
    // staleTime de 5min passar. refetchOnWindowFocus 'always' cobre esse caso:
    // ao voltar o foco pra essa aba, busca de novo, sem esperar o staleTime.
    refetchOnWindowFocus: 'always',
  });

  // Níveis realmente presentes entre as turmas da instituição (não só as do
  // turno atual — pra essa lista de opções não ficar pulando conforme troca
  // de aba). Turma sem nível nenhum (Cultural, Tecnológico, Capelania) nunca
  // aparece aqui, e por isso também nunca é afetada pelo filtro (ver abaixo).
  const niveisDisponiveis = useMemo(() => {
    const set = new Set();
    atividades.forEach((a) => nivelDaTurma(a.nome).forEach((n) => set.add(n)));
    return Array.from(set).sort(compararNiveis);
  }, [atividades]);

  // Só entram na grade as turmas que já têm dia/horário/turno definidos e que
  // ainda estão ativas — atividades sem horário não têm onde ser desenhadas, e
  // turmas encerradas não fazem mais parte da grade da semana (continuam
  // existindo pro histórico, só saem daqui; ver Turmas.js pra reabrir uma).
  // Nível vazio (niveisSelecionados) = sem filtro; turma sem nível nenhum
  // sempre aparece, mesmo com algum nível selecionado (mesma regra da área).
  const turmasDoTurno = useMemo(
    () =>
      atividades.filter((a) => {
        if (!(
          a.dia_semana &&
          a.horario &&
          a.turno === turnoSelecionado &&
          !a.data_fim &&
          (!a.area || areasAtivas[a.area] !== false)
        ))
          return false;
        if (niveisSelecionados.length === 0) return true;
        const niveisDaTurma = nivelDaTurma(a.nome);
        return (
          niveisDaTurma.length === 0 || niveisDaTurma.some((n) => niveisSelecionados.includes(n))
        );
      }),
    [atividades, turnoSelecionado, areasAtivas, niveisSelecionados],
  );

  const totalSemHorario = useMemo(
    () => atividades.filter((a) => !a.dia_semana || !a.horario || !a.turno).length,
    [atividades],
  );

  // Modo Atividade: grade dia × horário — cada célula pode ter mais de uma
  // turma (atividades diferentes podem ocupar o mesmo slot).
  const grade = useMemo(() => {
    const grid = {};
    DIAS_SEMANA.forEach((dia) => {
      grid[dia] = {};
      HORARIOS.forEach((horario) => {
        grid[dia][horario] = [];
      });
    });
    turmasDoTurno.forEach((t) => {
      const dia = (t.dia_semana || '').trim();
      const horario = (t.horario || '').trim();
      if (grid[dia] && grid[dia][horario]) {
        grid[dia][horario].push(t);
      }
    });
    return grid;
  }, [turmasDoTurno]);

  // HR1/HR2 sempre aparecem (horários padrão, mesmo vazios) — HR3 só entra
  // se alguma turma de verdade usar esse horário nesta grade, pra não
  // deixar uma linha inteira de "—" só de enfeite.
  const horariosVisiveis = useMemo(
    () =>
      HORARIOS.filter(
        (h) => h !== 'HR 3' || DIAS_SEMANA.some((dia) => (grade[dia]?.[h] || []).length > 0),
      ),
    [grade],
  );

  // Professores do turno com suas turmas — usado pro painel lateral (modo
  // Atividade, só a contagem) e pra mini-grade de cada professor (modo Professor).
  const professoresComTurmas = useMemo(() => {
    const porProfessor = new Map();
    turmasDoTurno.forEach((t) => {
      const prof = (t.nome_professor || '').trim();
      if (!prof) return;
      if (!porProfessor.has(prof)) porProfessor.set(prof, []);
      porProfessor.get(prof).push(t);
    });
    return Array.from(porProfessor.entries())
      .map(([nome, turmas]) => ({
        nome,
        turmas,
        atividade: Array.from(new Set(turmas.map((t) => t.nome))).join(', '),
        totalAlunos: turmas.reduce((s, t) => s + (Number(t.total_alunos) || 0), 0),
      }))
      .filter((p) => !buscaProfessor || p.nome.toLowerCase().includes(buscaProfessor.toLowerCase()))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [turmasDoTurno, buscaProfessor]);

  // Modo Professor: mesma lista de professores, sem o filtro de busca (a busca
  // nesse modo é só pro painel de atividades, não pra grade principal).
  const todosProfessoresComTurmas = useMemo(() => {
    const porProfessor = new Map();
    turmasDoTurno.forEach((t) => {
      const prof = (t.nome_professor || '').trim();
      if (!prof) return;
      if (!porProfessor.has(prof)) porProfessor.set(prof, []);
      porProfessor.get(prof).push(t);
    });
    return Array.from(porProfessor.entries())
      .map(([nome, turmas]) => ({ nome, turmas }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [turmasDoTurno]);

  // Modo Professor: painel lateral por Atividade.
  const atividadesFiltradas = useMemo(() => {
    return turmasDoTurno
      .filter((t) => !buscaAtividade || t.nome.toLowerCase().includes(buscaAtividade.toLowerCase()))
      .sort(
        (a, b) =>
          a.nome.localeCompare(b.nome, 'pt-BR') ||
          a.dia_semana.localeCompare(b.dia_semana, 'pt-BR') ||
          a.horario.localeCompare(b.horario, 'pt-BR'),
      );
  }, [turmasDoTurno, buscaAtividade]);

  const exportarRelatorio = () => {
    const linhas = turmasDoTurno
      .slice()
      .sort(
        (a, b) => a.dia_semana.localeCompare(b.dia_semana) || a.horario.localeCompare(b.horario),
      )
      .map((t) => ({
        Dia: t.dia_semana,
        Horário: t.horario,
        Turno: t.turno,
        Atividade: t.nome,
        Educador: t.nome_professor || '-',
        Inscritos: t.total_alunos,
      }));
    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Grade');
    XLSX.writeFile(workbook, `Grade_${turnoSelecionado}.xlsx`);
  };

  // --- Modal da turma: lista de matriculados, mover pra outra turma (de
  // qualquer área) e adicionar novo aluno — aberto ao clicar num card, tanto
  // no modo Atividade quanto no modo Professor. ---
  const [turmaSelecionada, setTurmaSelecionada] = useState(null);
  const [buscaAlunoModal, setBuscaAlunoModal] = useState('');
  const [processandoId, setProcessandoId] = useState(null); // id da matrícula (ou 'novo') em processamento

  const { data: rosterTurma = [], isLoading: carregandoRoster } = useQuery({
    queryKey: ['roster-turma-grade-turmas', turmaSelecionada?.id],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/matriculas/por-instituicao?id_atividade=${turmaSelecionada.id}`,
        { headers: authHeaders },
      );
      if (!res.ok) throw new Error('Erro ao carregar inscritos da turma');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!turmaSelecionada,
    refetchOnMount: 'always', // sempre atualizado ao abrir o card da turma, não só na primeira vez
    refetchOnWindowFocus: 'always',
  });

  // Seleção múltipla dos inscritos, pra mover vários (ou todos) de uma vez —
  // limpa sempre que a turma aberta no modal muda (troca de card, ou fecha).
  const [idsSelecionados, setIdsSelecionados] = useState(new Set());
  useEffect(() => {
    setIdsSelecionados(new Set());
  }, [turmaSelecionada?.id]);

  const alternarSelecao = (idMatricula) => {
    setIdsSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(idMatricula)) novo.delete(idMatricula);
      else novo.add(idMatricula);
      return novo;
    });
  };

  const todosSelecionados = rosterTurma.length > 0 && idsSelecionados.size === rosterTurma.length;
  const alternarSelecionarTodos = () => {
    setIdsSelecionados(todosSelecionados ? new Set() : new Set(rosterTurma.map((m) => m.id)));
  };

  const { data: resultadosBuscaAlunoModal = [] } = useQuery({
    queryKey: ['busca-aluno-grade-turmas', institutionId, buscaAlunoModal],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/alunos?nome=${encodeURIComponent(buscaAlunoModal)}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Erro ao buscar inscritos');
      const data = await res.json();
      const idsJaMatriculados = new Set(rosterTurma.map((m) => m.aluno_id));
      return (Array.isArray(data) ? data : []).filter((a) => !idsJaMatriculados.has(a.id));
    },
    enabled: !!buscaAlunoModal.trim() && !!turmaSelecionada,
  });

  // Destino do "mover" — qualquer turma ativa da instituição, de qualquer
  // área/dia/turno, exceto a própria turma aberta no modal. Rótulo mostra
  // área junto pro usuário achar mais fácil (várias turmas compartilham nome).
  const opcoesMoverTurma = useMemo(() => {
    if (!turmaSelecionada) return [];
    return atividades
      .filter((a) => !a.data_fim && a.id !== turmaSelecionada.id)
      .map((a) => ({
        value: a.id,
        label: `${a.nome} — ${a.dia_semana} ${a.horario} (${a.turno})${a.area ? ` · ${AREA_LABEL[a.area] || a.area}` : ''}`,
      }))
      .sort((x, y) => x.label.localeCompare(y.label, 'pt-BR'));
  }, [atividades, turmaSelecionada]);

  // "Mover para..." também precisa de busca — a instituição pode ter centenas
  // de turmas, então um <select> nativo listando todas fica impraticável.
  // `matriculasParaMover` é sempre um array — 1 item quando vem do botão
  // "Mover" de uma linha, N itens quando vem da barra de seleção múltipla —
  // assim o mesmo modal de escolher destino serve pros dois casos.
  const [matriculasParaMover, setMatriculasParaMover] = useState([]);
  const [buscaMoverTurma, setBuscaMoverTurma] = useState('');
  const [movendoEmLote, setMovendoEmLote] = useState(false);

  const opcoesMoverFiltradas = useMemo(() => {
    const termo = buscaMoverTurma.trim().toLowerCase();
    if (!termo) return opcoesMoverTurma;
    return opcoesMoverTurma.filter((o) => o.label.toLowerCase().includes(termo));
  }, [opcoesMoverTurma, buscaMoverTurma]);

  const recarregarRoster = () => {
    queryClient.invalidateQueries({
      queryKey: ['roster-turma-grade-turmas', turmaSelecionada?.id],
    });
    queryClient.invalidateQueries({ queryKey: ['atividades-grade-turmas', institutionId] }); // atualiza contagem nos cards
  };

  const adicionarAlunoNaTurma = async (aluno) => {
    setProcessandoId('novo');
    try {
      const res = await fetch(`${API_URL}/api/matriculas/matricular`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ aluno_id: aluno.id, id_atividade: turmaSelecionada.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao matricular inscrito');
      setBuscaAlunoModal('');
      toast.success(`${aluno.nome} matriculado(a) nessa turma.`);
      recarregarRoster();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  // Encerrar (soft-close) direto do modal da turma — só oferecido quando ela
  // já está vazia (ver botão condicional no JSX), então nunca desmatricula
  // ninguém de verdade aqui; é o mesmo endpoint que Turmas.js já usa.
  const encerrarTurmaVazia = async (turma) => {
    if (
      !(await confirm(
        `Encerrar "${turma.nome}"? Ela sai da grade ativa, mas o histórico continua registrado — pode reabrir depois em Turmas.`,
        { title: 'Encerrar turma', confirmText: 'Encerrar', tone: 'danger' },
      ))
    )
      return;
    setProcessandoId('encerrar');
    try {
      await api.post(`/api/atividades/${turma.id}/encerrar`);
      toast.success(`"${turma.nome}" encerrada.`);
      setTurmaSelecionada(null);
      queryClient.invalidateQueries({ queryKey: ['atividades-grade-turmas', institutionId] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  const removerAlunoDaTurma = async (matricula) => {
    if (
      !(await confirm(`Remover ${matricula.nome_aluno} dessa turma?`, {
        title: 'Remover inscrito',
        confirmText: 'Remover',
      }))
    )
      return;
    setProcessandoId(matricula.id);
    try {
      await api.del(`/api/matriculas/${matricula.id}`);
      toast.success('Inscrito removido dessa turma.');
      recarregarRoster();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  // POST /matriculas/mover encerra a matrícula atual e cria a nova numa
  // transação só no backend — evita o aluno ficar sem matrícula nenhuma se a
  // segunda metade falhasse (risco real de fazer isso como dois requests
  // separados daqui, que era como funcionava antes). Aceita 1 ou N
  // matrículas de uma vez (botão "Mover" de uma linha = 1; barra de seleção
  // múltipla = N) — cada aluno é um request/transação independente, então um
  // erro no meio não trava nem desfaz os que já foram movidos com sucesso.
  const moverMatriculasParaTurma = async (matriculas, idNovaTurma) => {
    const destino = opcoesMoverTurma.find((o) => String(o.value) === String(idNovaTurma));
    if (!destino) return;
    const pergunta =
      matriculas.length === 1
        ? `Mover ${matriculas[0].nome_aluno} para "${destino.label}"?`
        : `Mover ${matriculas.length} inscritos para "${destino.label}"?`;
    if (
      !(await confirm(pergunta, {
        title: 'Mover inscrito(s)',
        confirmText: 'Mover',
        tone: 'primary',
      }))
    )
      return;

    setMovendoEmLote(true);
    let sucesso = 0;
    const falhas = [];
    for (const matricula of matriculas) {
      setProcessandoId(matricula.id);
      try {
        const res = await fetch(`${API_URL}/api/matriculas/mover`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ matricula_id: matricula.id, id_atividade_destino: idNovaTurma }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erro ao mover inscrito de turma');
        sucesso += 1;
      } catch (err) {
        falhas.push(`${matricula.nome_aluno}: ${err.message}`);
      }
    }
    setProcessandoId(null);
    setMovendoEmLote(false);

    if (sucesso > 0) {
      toast.success(
        matriculas.length === 1
          ? `${matriculas[0].nome_aluno} movido(a) para "${destino.label}".`
          : `${sucesso} inscrito${sucesso === 1 ? '' : 's'} movido(s) para "${destino.label}".`,
      );
    }
    if (falhas.length > 0) {
      toast.error(`Não foi possível mover: ${falhas.join('; ')}`);
    }
    setIdsSelecionados(new Set());
    recarregarRoster();
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-white dark:bg-gray-900 p-1 rounded-xl border border-gray-200 dark:border-gray-700 inline-flex items-center">
              {TURNOS.map((turno) => (
                <button
                  key={turno}
                  onClick={() => setTurnoSelecionado(turno)}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    turnoSelecionado === turno
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  {turno}
                </button>
              ))}
            </div>

            <div className="bg-white dark:bg-gray-900 p-1 rounded-xl border border-gray-200 dark:border-gray-700 inline-flex items-center">
              <button
                onClick={() => setModo('atividade')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  modo === 'atividade'
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Atividade
              </button>
              <button
                onClick={() => setModo('professor')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  modo === 'professor'
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Educador
              </button>
            </div>
          </div>

          {podeExportar && (
            <button
              onClick={exportarRelatorio}
              disabled={turmasDoTurno.length === 0}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Exportar Relatório
            </button>
          )}
        </div>

        {/* Filtro por área: todas ligadas por padrão; clicar apaga a cor e
            esconde as turmas daquela área da grade (ver turmasDoTurno acima). */}
        <div className="flex flex-wrap items-center gap-2 -mt-2">
          {AREAS.map((a) => {
            const ativa = areasAtivas[a.value] !== false;
            return (
              <button
                key={a.value}
                onClick={() => alternarArea(a.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${ativa ? a.ativo : a.inativo}`}
                title={ativa ? `Ocultar turmas de ${a.label}` : `Mostrar turmas de ${a.label}`}
              >
                {a.label}
              </button>
            );
          })}
        </div>

        {/* Filtro por nível: ao contrário da área, começa sem nada marcado
            (mostra tudo) — marcar um ou mais nível estreita a grade só pras
            turmas daqueles níveis (mais as sem nível nenhum, que nunca somem). */}
        {niveisDisponiveis.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 -mt-2">
            <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Nível:</span>
            {niveisDisponiveis.map((nivel) => {
              const ativo = niveisSelecionados.includes(nivel);
              return (
                <button
                  key={nivel}
                  onClick={() => alternarNivel(nivel)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    ativo
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {nivel}
                </button>
              );
            })}
            {niveisSelecionados.length > 0 && (
              <button
                onClick={() => setNiveisSelecionados([])}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
              >
                Limpar
              </button>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin mr-3"></div>
            <span className="text-sm">Carregando grade...</span>
          </div>
        ) : modo === 'atividade' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card padding="none" className="overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Grade Horária — {turnoSelecionado}
                  </h2>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {turmasDoTurno.length} turmas
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 w-20">
                          Horário
                        </th>
                        {DIAS_SEMANA.map((dia) => (
                          <th
                            key={dia}
                            className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800"
                          >
                            {dia}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {horariosVisiveis.map((horario) => (
                        <tr
                          key={horario}
                          className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                        >
                          <td className="px-3 py-2 font-medium text-gray-400 dark:text-gray-500 align-top">
                            {horario}
                          </td>
                          {DIAS_SEMANA.map((dia) => {
                            const turmas = grade[dia]?.[horario] || [];
                            return (
                              <td key={dia} className="px-2 py-2 align-top">
                                {turmas.length === 0 ? (
                                  <span className="text-gray-300 dark:text-gray-700">—</span>
                                ) : (
                                  <div className="space-y-1">
                                    {turmas.map((t) => {
                                      const cor = corDaAtividade(t.nome);
                                      return (
                                        <button
                                          type="button"
                                          key={t.id}
                                          onClick={() => setTurmaSelecionada(t)}
                                          className={`relative w-full text-left px-2 py-1 rounded-lg border transition-shadow hover:shadow-sm cursor-pointer ${cor.bg} ${cor.border}`}
                                        >
                                          {t.total_alunos > 0 && (
                                            <span
                                              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-gray-900"
                                              title={`${t.total_alunos} inscrito${t.total_alunos === 1 ? '' : 's'}`}
                                            >
                                              {t.total_alunos}
                                            </span>
                                          )}
                                          <p
                                            className={`text-xs font-medium ${cor.text}`}
                                            translate="no"
                                          >
                                            {t.nome}
                                          </p>
                                          {t.nome_professor && (
                                            <p
                                              className="text-[10px] text-gray-500 dark:text-gray-400"
                                              translate="no"
                                            >
                                              {t.nome_professor}
                                            </p>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              {totalSemHorario > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                  {totalSemHorario} atividade{totalSemHorario === 1 ? '' : 's'} cadastrada
                  {totalSemHorario === 1 ? '' : 's'} ainda sem dia/horário/turno definidos não
                  aparece{totalSemHorario === 1 ? '' : 'm'} nesta grade.
                </p>
              )}
            </div>

            <div>
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Educadores Alocados — {turnoSelecionado}
                  </h2>
                </div>
                <Input
                  placeholder="Buscar educador..."
                  icon={Search}
                  value={buscaProfessor}
                  onChange={(e) => setBuscaProfessor(e.target.value)}
                  className="mb-3"
                />
                <div className="space-y-2 max-h-[480px] overflow-y-auto">
                  {professoresComTurmas.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">
                      Nenhum educador alocado nesse turno.
                    </p>
                  ) : (
                    professoresComTurmas.map((prof) => (
                      <div
                        key={prof.nome}
                        className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800"
                      >
                        <div className="min-w-0">
                          <p
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                            translate="no"
                          >
                            {prof.nome}
                          </p>
                          <p
                            className="text-xs text-gray-500 dark:text-gray-400 truncate"
                            translate="no"
                          >
                            {prof.atividade}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <Badge tone="info">
                            {prof.turmas.length} turma{prof.turmas.length === 1 ? '' : 's'}
                          </Badge>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {prof.totalAlunos} inscritos
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Educadores — {turnoSelecionado}
                </h2>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {todosProfessoresComTurmas.length} educadores
                </span>
              </div>
              {todosProfessoresComTurmas.length === 0 ? (
                <Card>
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum educador alocado nesse turno.
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {todosProfessoresComTurmas.map((prof) => {
                    // Mesma regra de HR3 opcional, mas por educador: cada um
                    // tem sua própria mini-grade, então HR3 pode estar em uso
                    // pra um e vazio pra outro.
                    const horariosDoProf = HORARIOS.filter(
                      (h) =>
                        h !== 'HR 3' ||
                        prof.turmas.some((t) => (t.horario || '').trim() === 'HR 3'),
                    );
                    return (
                      <Card key={prof.nome} padding="none" className="overflow-hidden">
                        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
                          <p
                            className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                            translate="no"
                          >
                            {prof.nome}
                          </p>
                          <Badge tone="info">
                            {prof.turmas.length} turma{prof.turmas.length === 1 ? '' : 's'}
                          </Badge>
                        </div>
                        <div className="p-2 overflow-x-auto">
                          <table className="w-full text-[10px] border-collapse">
                            <thead>
                              <tr>
                                <th className="text-left text-gray-400 dark:text-gray-500 font-medium w-8">
                                  Dia
                                </th>
                                {horariosDoProf.map((h) => (
                                  <th
                                    key={h}
                                    className="text-center text-gray-400 dark:text-gray-500 font-medium"
                                  >
                                    {h.replace('HR ', 'H')}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {DIAS_SEMANA.map((dia) => (
                                <tr key={dia}>
                                  <td className="text-gray-500 dark:text-gray-400 font-medium py-1 align-top">
                                    {dia.slice(0, 3)}
                                  </td>
                                  {horariosDoProf.map((horario) => {
                                    // .filter (não .find): pode haver mais de uma turma do mesmo
                                    // educador no mesmo dia+horário (ex.: duas turmas pequenas
                                    // dividindo o mesmo slot) — usar .find() aqui escondia
                                    // silenciosamente todas menos a primeira, dando a impressão de
                                    // turma "desaparecida" ou "no lugar errado".
                                    const turmasNoSlot = prof.turmas.filter(
                                      (t) =>
                                        (t.dia_semana || '').trim() === dia &&
                                        (t.horario || '').trim() === horario,
                                    );
                                    return (
                                      <td
                                        key={horario}
                                        className="text-center py-1 px-0.5 align-top"
                                      >
                                        {turmasNoSlot.length === 0 ? (
                                          <span className="text-gray-300 dark:text-gray-700">
                                            -
                                          </span>
                                        ) : (
                                          <div className="flex flex-col items-center gap-0.5">
                                            {turmasNoSlot.map((turma) => {
                                              const cor = corDaAtividade(turma.nome);
                                              return (
                                                <button
                                                  type="button"
                                                  key={turma.id}
                                                  onClick={() => setTurmaSelecionada(turma)}
                                                  className="relative inline-block w-[70px] cursor-pointer"
                                                >
                                                  {turma.total_alunos > 0 && (
                                                    <span
                                                      className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-gray-900 z-10"
                                                      title={`${turma.total_alunos} inscrito${turma.total_alunos === 1 ? '' : 's'}`}
                                                    >
                                                      {turma.total_alunos}
                                                    </span>
                                                  )}
                                                  <span
                                                    className={`block w-full px-1 py-0.5 rounded border truncate ${cor.bg} ${cor.text} ${cor.border}`}
                                                    translate="no"
                                                    title={`${turma.nome} — ${turma.total_alunos} inscritos`}
                                                  >
                                                    {turma.nome}
                                                  </span>
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <GraduationCap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Atividades — {turnoSelecionado}
                  </h2>
                </div>
                <Input
                  placeholder="Buscar atividade..."
                  icon={Search}
                  value={buscaAtividade}
                  onChange={(e) => setBuscaAtividade(e.target.value)}
                  className="mb-3"
                />
                <div className="space-y-2 max-h-[480px] overflow-y-auto">
                  {atividadesFiltradas.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">
                      Nenhuma turma encontrada.
                    </p>
                  ) : (
                    atividadesFiltradas.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800"
                      >
                        <div className="min-w-0">
                          <p
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                            translate="no"
                          >
                            {t.nome}
                          </p>
                          <p
                            className="text-xs text-gray-500 dark:text-gray-400 truncate"
                            translate="no"
                          >
                            {t.nome_professor || 'Sem educador'} · {t.dia_semana} {t.horario}
                          </p>
                        </div>
                        <Badge tone="info">{t.total_alunos}</Badge>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Modal: alunos da turma — mover pra outra turma (qualquer área) ou adicionar */}
      <Modal
        isOpen={!!turmaSelecionada}
        onClose={() => {
          setTurmaSelecionada(null);
          setBuscaAlunoModal('');
        }}
        title={turmaSelecionada ? `${turmaSelecionada.nome}` : 'Turma'}
        maxWidth="lg"
      >
        {turmaSelecionada && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-xl p-2.5 border border-gray-100 dark:border-gray-800">
              <span translate="no">{turmaSelecionada.nome_professor || 'Sem educador'}</span>
              <span>·</span>
              <span>
                {turmaSelecionada.dia_semana} {turmaSelecionada.horario} ({turmaSelecionada.turno})
              </span>
              {turmaSelecionada.area && (
                <>
                  <span>·</span>
                  <Badge tone="neutral">
                    {AREA_LABEL[turmaSelecionada.area] || turmaSelecionada.area}
                  </Badge>
                </>
              )}
            </div>

            {podeEditar && !carregandoRoster && rosterTurma.length === 0 && (
              <div className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Essa turma não tem nenhum inscrito.
                </p>
                <button
                  type="button"
                  onClick={() => encerrarTurmaVazia(turmaSelecionada)}
                  disabled={processandoId === 'encerrar'}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  <PowerOff className="w-3.5 h-3.5" />
                  Encerrar turma
                </button>
              </div>
            )}

            {podeEditar && (
              <div className="relative">
                <Input
                  placeholder="Buscar inscrito pelo nome para adicionar..."
                  icon={Search}
                  value={buscaAlunoModal}
                  onChange={(e) => setBuscaAlunoModal(e.target.value)}
                  disabled={processandoId === 'novo'}
                />
                {buscaAlunoModal.trim() && resultadosBuscaAlunoModal.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-auto">
                    {resultadosBuscaAlunoModal.map((aluno) => (
                      <button
                        key={aluno.id}
                        type="button"
                        onClick={() => adicionarAlunoNaTurma(aluno)}
                        disabled={processandoId === 'novo'}
                        className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 disabled:opacity-50"
                      >
                        <span translate="no">{aluno.nome}</span>
                        <span className="text-blue-600 dark:text-blue-400 text-xs font-semibold">
                          Adicionar
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer">
                  {rosterTurma.length > 0 && (
                    <input
                      type="checkbox"
                      checked={todosSelecionados}
                      onChange={alternarSelecionarTodos}
                      className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                  Inscritos matriculados ({rosterTurma.length})
                </label>
                {podeEditar && idsSelecionados.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {idsSelecionados.size} selecionado{idsSelecionados.size === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      disabled={movendoEmLote}
                      onClick={() => {
                        setMatriculasParaMover(
                          rosterTurma.filter((m) => idsSelecionados.has(m.id)),
                        );
                        setBuscaMoverTurma('');
                      }}
                      className="inline-flex items-center gap-1 px-2 h-7 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      <ArrowRightLeft className="w-3 h-3" />
                      Mover selecionados
                    </button>
                    <button
                      type="button"
                      disabled={movendoEmLote}
                      onClick={() => setIdsSelecionados(new Set())}
                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {carregandoRoster ? (
                  <div className="flex items-center justify-center py-6 text-gray-400">
                    <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                ) : rosterTurma.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                    Nenhum inscrito matriculado ainda.
                  </p>
                ) : (
                  rosterTurma.map((m) => (
                    <div
                      key={m.id}
                      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 ${corCardNivel(m.nivel) || 'bg-gray-50/60 dark:bg-gray-800/40'}`}
                    >
                      <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={idsSelecionados.has(m.id)}
                          onChange={() => alternarSelecao(m.id)}
                          className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                        />
                        <span
                          className="text-sm text-gray-900 dark:text-gray-100 truncate"
                          translate="no"
                        >
                          {m.nome_aluno}
                        </span>
                      </label>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {podeEditar && (
                          <button
                            onClick={() => {
                              setMatriculasParaMover([m]);
                              setBuscaMoverTurma('');
                            }}
                            disabled={processandoId === m.id || movendoEmLote}
                            title="Mover para outra turma"
                            className="h-7 px-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
                          >
                            <ArrowRightLeft className="w-3 h-3" />
                            <span className="text-xs">Mover</span>
                          </button>
                        )}
                        {podeEditar && (
                          <button
                            onClick={() => removerAlunoDaTurma(m)}
                            disabled={processandoId === m.id || movendoEmLote}
                            title="Remover dessa turma"
                            className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: buscar turma de destino pro "mover" — separado do modal principal
          pra não ficar preso ao scroll da lista de alunos matriculados. Serve
          tanto pro "Mover" de uma linha (1 matrícula) quanto pra barra de
          seleção múltipla (N matrículas) — ver moverMatriculasParaTurma. */}
      <Modal
        isOpen={matriculasParaMover.length > 0}
        onClose={() => setMatriculasParaMover([])}
        title={
          matriculasParaMover.length === 1
            ? `Mover ${matriculasParaMover[0].nome_aluno}`
            : matriculasParaMover.length > 1
              ? `Mover ${matriculasParaMover.length} inscritos`
              : 'Mover inscrito'
        }
        maxWidth="md"
      >
        {matriculasParaMover.length > 0 && (
          <div className="space-y-3">
            <Input
              placeholder="Buscar turma por nome, dia, educador ou área..."
              icon={Search}
              value={buscaMoverTurma}
              onChange={(e) => setBuscaMoverTurma(e.target.value)}
            />
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {opcoesMoverFiltradas.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                  Nenhuma turma encontrada.
                </p>
              ) : (
                opcoesMoverFiltradas.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={async () => {
                      const matriculas = matriculasParaMover;
                      setMatriculasParaMover([]);
                      await moverMatriculasParaTurma(matriculas, o.value);
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-900/40 disabled:opacity-50"
                    translate="no"
                  >
                    {o.label}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default GradeTurmas;
